import React, { useState } from 'react';
import axios from 'axios';
import { IconType } from 'react-icons';
import {
  FiAlertTriangle,
  FiBell,
  FiCheck,
  FiClock,
  FiEdit2,
  FiInfo,
  FiLock,
  FiMail,
  FiMessageSquare,
  FiPrinter,
  FiRotateCw,
  FiShield,
  FiSlash,
  FiUser,
} from 'react-icons/fi';
import { QueryKey, useMutation, useQueryClient } from 'react-query';

import Button from '../../components/Button';
import ToastNotification from '../../components/ToastNotification';
import DefaultModal from '../../components/modals/DefaultModal';
import { approveTicketOnMemberBehalf, resendApprovalEmail } from './api';
import {
  APPROVAL_REASON_MAX_LENGTH,
  APPROVAL_REASON_MIN_LENGTH,
  approvalChipLabel,
  approvalDecidedByLabel,
  approvalHeadline,
  buildApprovalConversation,
  canActOnApproval,
  formatDate,
  formatDateTime,
  getApiErrorMessage,
  isApprovedWithoutRevision,
  isItpPendingStatus,
  isPrinterHeldForItp,
  isPrinterSending,
  PrinterReleaseContext,
  validateApprovalReason,
} from './helpers';
import { formatMessage, printShopStrings } from './strings';
import {
  ApprovalConversationEntry,
  ApprovalConversationIcon,
  ApprovalConversationTone,
  ApprovalHeadline,
  TicketApproval,
  TicketDetail,
} from './types';

/**
 * The member-approval card of a book ticket (book-order-approval F9, F10,
 * F14), laid out as an action card and a conversation.
 *
 * Left: the state in plain words, what happens next, the two support actions
 * while the ticket sits in an approval lane, and only the facts that matter
 * in that state. Right: the approval story oldest first — system events as
 * small lines, the member's comment and support's reason as messages.
 *
 * Approved and bypassed tickets keep the card, read-only, so the decision
 * stays visible after the order has moved on to the print shop.
 *
 * Kept out of `TicketDetail` for the same reason as `BookTicketPanels`: it is
 * book-only, and the page is already long.
 */

const STRINGS = printShopStrings.detail.approval;

/**
 * The darker teal of the approved design, scoped to this card's own action
 * buttons. The shared PRIMARY button (white on primary-500) is about 2:1;
 * this is above 5:1. Global theme tokens stay as they are.
 */
const FILLED_ACTION_CLASS =
  'border-[#1f7383] bg-[#1f7383] text-white hover:border-[#165a67] hover:bg-[#165a67]';
const OUTLINED_ACTION_CLASS =
  'border-primary-300 bg-white text-[#1f7383] hover:bg-primary-50';
const ACTION_BASE_CLASS =
  'inline-flex h-11 items-center justify-center gap-2 rounded-lg border px-4 text-sm font-bold transition-colors focus:outline-none focus:ring-2 focus:ring-[#1f7383] focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60';

const CHIP_CLASS: Record<ApprovalHeadline, string> = {
  awaiting: 'bg-indigo-50 text-indigo-700',
  awaiting_expired: 'bg-indigo-50 text-indigo-700',
  changes_requested: 'bg-pink-50 text-pink-800',
  approved_by_member: 'bg-success-50 text-success-700',
  approved_by_support: 'bg-success-50 text-success-700',
  approved: 'bg-success-50 text-success-700',
  bypassed: 'bg-neutral-100 text-neutral-700',
  closed: 'bg-neutral-100 text-neutral-700',
};

const HEADLINE_ICON: Record<
  ApprovalHeadline,
  { icon: IconType; className: string }
> = {
  awaiting: { icon: FiClock, className: 'bg-indigo-50 text-indigo-700' },
  awaiting_expired: {
    icon: FiAlertTriangle,
    className: 'bg-error-50 text-error-700',
  },
  changes_requested: {
    icon: FiMessageSquare,
    className: 'bg-pink-50 text-pink-800',
  },
  approved_by_member: {
    icon: FiCheck,
    className: 'bg-success-50 text-success-700',
  },
  approved_by_support: {
    icon: FiCheck,
    className: 'bg-success-50 text-success-700',
  },
  approved: { icon: FiCheck, className: 'bg-success-50 text-success-700' },
  bypassed: { icon: FiSlash, className: 'bg-neutral-100 text-neutral-700' },
  closed: { icon: FiLock, className: 'bg-neutral-100 text-neutral-700' },
};

const ENTRY_ICON: Record<ApprovalConversationIcon, IconType> = {
  mail: FiMail,
  bell: FiBell,
  refresh: FiRotateCw,
  edit: FiEdit2,
  clock: FiClock,
  check: FiCheck,
  printer: FiPrinter,
  alert: FiAlertTriangle,
  slash: FiSlash,
};

const ENTRY_TONE_CLASS: Record<
  ApprovalConversationTone,
  { icon: string; text: string }
> = {
  neutral: {
    icon: 'bg-neutral-100 text-neutral-600',
    text: 'text-neutral-700',
  },
  success: { icon: 'bg-success-50 text-success-700', text: 'text-neutral-700' },
  danger: {
    icon: 'bg-error-50 text-error-700',
    text: 'font-semibold text-error-700',
  },
};

/** The DS answer when another decision won the race (techspec §3.5). */
const isConflict = (error: unknown): boolean =>
  axios.isAxiosError(error) && error.response?.status === 409;

/**
 * Renders a copy template with one token replaced by a bold node, so the copy
 * stays whole in `strings.ts` and only the number is emphasised.
 */
const withBoldToken = (
  template: string,
  token: string,
  value: string,
): React.ReactNode => {
  const [before, after] = template.split(`{${token}}`);
  if (after === undefined) return template;
  return (
    <>
      {before}
      <strong className="font-bold text-neutral-900">{value}</strong>
      {after}
    </>
  );
};

const Callout = ({
  testId,
  tone,
  children,
}: {
  testId: string;
  tone: 'info' | 'danger';
  children: React.ReactNode;
}) => (
  <p
    data-testid={testId}
    className={`flex items-start gap-2 rounded-lg border px-3 py-2.5 text-[13px] leading-[18px] ${
      tone === 'danger'
        ? 'border-error-100 bg-error-50 text-error-700'
        : 'border-neutral-200 bg-white text-neutral-700'
    }`}
  >
    {tone === 'danger' ? (
      <FiAlertTriangle aria-hidden className="mt-px h-4 w-4 flex-none" />
    ) : (
      <FiInfo aria-hidden className="mt-px h-4 w-4 flex-none text-[#1f7383]" />
    )}
    <span>{children}</span>
  </p>
);

type Fact = {
  key: string;
  label: string;
  value: React.ReactNode;
  hint?: string;
};

const printerFact = (
  approval: TicketApproval,
  now: number,
  release: PrinterReleaseContext,
): Fact => {
  const status = approval.printer?.status;
  let value: React.ReactNode = STRINGS.printerStatuses[status] ?? status ?? '—';
  if (isPrinterHeldForItp(approval, release)) {
    // Held for the ITP: not sent, and nothing is retrying it yet.
    value = STRINGS.printerStatuses.held;
  } else if (isPrinterSending(approval, now, release)) {
    value = STRINGS.facts.printerSending;
  } else if (status === 'sent') {
    value = (
      <span className="inline-flex items-center gap-1.5 text-success-700">
        <FiCheck aria-hidden className="h-3.5 w-3.5 flex-none" />
        {approval.printer.at
          ? formatMessage(STRINGS.facts.printerSentAt, {
              at: formatDateTime(approval.printer.at),
            })
          : STRINGS.printerStatuses.sent}
      </span>
    );
  } else if (status === 'failed') {
    value = (
      <span className="inline-flex items-center gap-1.5 text-error-700">
        <FiAlertTriangle aria-hidden className="h-3.5 w-3.5 flex-none" />
        {STRINGS.printerStatuses.failed}
      </span>
    );
  }
  return { key: 'printer', label: STRINGS.facts.printer, value };
};

/** Only the facts that answer something in the current state. */
const factsFor = (
  ticket: TicketDetail,
  approval: TicketApproval,
  headline: ApprovalHeadline,
  now: number,
): Fact[] => {
  const release: PrinterReleaseContext = { status: ticket.status, itp: ticket.itp };
  const copies: Fact = {
    key: 'copies',
    label: STRINGS.facts.copies,
    value: String(ticket.copies ?? ticket.quantity ?? '—'),
  };
  switch (headline) {
    case 'awaiting': {
      const facts: Fact[] = [
        copies,
        {
          key: 'link',
          label: STRINGS.facts.linkValidUntil,
          value: formatDate(approval.expiresAt),
        },
      ];
      // The reminder's due time is not in the API: say it has not gone out
      // and when it would.
      if (!approval.reminderSentAt && approval.lastEmail?.kind !== 'reminder') {
        facts.push({
          key: 'reminder',
          label: STRINGS.facts.reminder,
          value: STRINGS.facts.reminderNotSent,
          hint: STRINGS.facts.reminderHint,
        });
      }
      return facts;
    }
    case 'awaiting_expired':
      return [
        copies,
        {
          key: 'link',
          label: STRINGS.facts.linkValidUntil,
          value: (
            <span className="text-error-700">
              {formatMessage(STRINGS.facts.linkExpired, {
                date: formatDate(approval.expiresAt),
              })}
            </span>
          ),
        },
      ];
    case 'changes_requested':
      return [
        copies,
        {
          key: 'link',
          label: STRINGS.facts.approvalLink,
          value: STRINGS.facts.linkAnswered,
        },
      ];
    case 'closed':
      return [copies];
    case 'bypassed':
      return [
        {
          key: 'decided-at',
          label: STRINGS.facts.releasedAt,
          value: formatDateTime(approval.decidedAt ?? undefined),
        },
        printerFact(approval, now, release),
        copies,
      ];
    default:
      return [
        {
          key: 'decided-at',
          label: STRINGS.facts.approvedAt,
          value: formatDateTime(approval.decidedAt ?? undefined),
        },
        {
          key: 'decided-by',
          label: STRINGS.facts.approvedBy,
          value: approvalDecidedByLabel(approval.decidedBy),
        },
        printerFact(approval, now, release),
        copies,
      ];
  }
};

/** The headline's next-step sentence, with the printer's state once released. */
const nextStepFor = (
  ticket: TicketDetail,
  approval: TicketApproval,
  headline: ApprovalHeadline,
  now: number,
): string => {
  // A cancelled order is closed: no approval, reminder or printer e-mail is
  // still coming, whatever the approval state was when it was cancelled.
  if (ticket.status === 'cancelled') return STRINGS.headlines.closed.body;
  const release: PrinterReleaseContext = { status: ticket.status, itp: ticket.itp };
  const held = isPrinterHeldForItp(approval, release);
  // An ITP order waits for its ITP; it never went straight to Paid.
  const template =
    headline === 'bypassed' && (ticket.itp || held)
      ? STRINGS.bypassedItpBody
      : STRINGS.headlines[headline].body;
  const body = formatMessage(template, {
    actor:
      headline === 'approved_by_support'
        ? approval.decidedBy?.id ?? approvalDecidedByLabel(approval.decidedBy)
        : approvalDecidedByLabel(approval.decidedBy),
  });
  const released =
    approval.state === 'approved' || approval.state === 'bypassed';
  let printerNext: string | undefined;
  if (released && held) {
    printerNext = STRINGS.printerNext.held;
  } else if (released) {
    printerNext = isPrinterSending(approval, now, release)
      ? STRINGS.printerNextSending
      : STRINGS.printerNext[approval.printer?.status];
  }
  return printerNext ? `${body} ${printerNext}` : body;
};

const EntryTime = ({ at }: { at: string | null }) =>
  at ? (
    <time
      dateTime={at}
      className="flex-none text-xs leading-4 text-neutral-500"
    >
      {formatDateTime(at)}
    </time>
  ) : null;

const ConversationEntry = ({ entry }: { entry: ApprovalConversationEntry }) => {
  switch (entry.kind) {
    case 'member_comment':
      return (
        <li
          data-testid="approval-entry"
          data-kind={entry.kind}
          className="flex items-start gap-2.5 py-1"
        >
          <span className="inline-flex h-7 w-7 flex-none items-center justify-center rounded-full bg-pink-50 text-pink-800">
            <FiUser aria-hidden className="h-3.5 w-3.5" />
          </span>
          <div className="flex min-w-0 max-w-[440px] flex-col gap-1.5">
            <div className="flex flex-wrap items-baseline gap-x-2">
              <span className="text-[13px] font-bold text-neutral-800">
                {entry.author}
              </span>
              <span className="text-xs text-neutral-500">
                {entry.label} · {formatDateTime(entry.at)}
              </span>
            </div>
            {/* The member's own words. React escapes them; pre-wrap keeps the
                line breaks they typed. Never rendered as HTML. */}
            <p
              data-testid="approval-changes-comment"
              className="whitespace-pre-wrap break-words rounded-[4px_12px_12px_12px] border border-neutral-200 bg-white px-3.5 py-2.5 text-sm text-neutral-900"
            >
              {entry.comment}
            </p>
          </div>
        </li>
      );
    case 'support_reason':
      return (
        <li
          data-testid="approval-entry"
          data-kind={entry.kind}
          className="flex items-start justify-end gap-2.5 py-1"
        >
          <div className="flex min-w-0 max-w-[440px] flex-col items-end gap-1.5">
            {/* Stacked on narrow screens, author first; one line from sm up. */}
            <div className="flex flex-col items-end text-right sm:flex-row sm:flex-wrap sm:items-baseline sm:justify-end sm:gap-x-2">
              <span className="text-xs text-neutral-500">
                {entry.label}
                {entry.at ? ` · ${formatDateTime(entry.at)}` : ''}
              </span>
              <span className="order-first text-[13px] font-bold text-neutral-800 sm:order-none">
                {entry.author}
              </span>
            </div>
            {/* Operator-typed text: rendered as text, never as markup. */}
            <p
              data-testid="approval-reason"
              className="whitespace-pre-wrap break-words rounded-[12px_4px_12px_12px] border border-primary-100 bg-primary-50 px-3.5 py-2.5 text-sm text-neutral-900"
            >
              {entry.reason}
            </p>
            <span
              data-testid="approval-reason-note"
              className="inline-flex items-center gap-1.5 text-xs text-neutral-600"
            >
              <FiLock aria-hidden className="h-3 w-3 flex-none" />
              {STRINGS.conversation.supportReasonNote}
            </span>
          </div>
          <span className="inline-flex h-7 w-7 flex-none items-center justify-center rounded-full bg-primary-50 text-[#1f7383]">
            <FiShield aria-hidden className="h-3.5 w-3.5" />
          </span>
        </li>
      );
    case 'pending':
      return (
        <li
          data-testid="approval-entry"
          data-kind={entry.kind}
          className="flex min-h-7 items-center gap-2.5"
        >
          <span className="box-border inline-flex h-7 w-7 flex-none items-center justify-center rounded-full border border-dashed border-neutral-400 text-neutral-600">
            <FiClock aria-hidden className="h-3.5 w-3.5" />
          </span>
          <span className="text-[13px] italic leading-[18px] text-neutral-600">
            {entry.text}
          </span>
        </li>
      );
    default: {
      const Icon = ENTRY_ICON[entry.icon] ?? FiClock;
      const tone = ENTRY_TONE_CLASS[entry.tone] ?? ENTRY_TONE_CLASS.neutral;
      return (
        <li
          data-testid="approval-entry"
          data-kind={entry.kind}
          data-tone={entry.tone}
          className="flex min-h-7 items-center gap-2.5"
        >
          <span
            className={`inline-flex h-7 w-7 flex-none items-center justify-center rounded-full ${tone.icon}`}
          >
            <Icon aria-hidden className="h-3.5 w-3.5" />
          </span>
          <span
            className={`min-w-0 flex-1 text-[13px] leading-[18px] ${tone.text}`}
          >
            {entry.text}
          </span>
          <EntryTime at={entry.at} />
        </li>
      );
    }
  }
};

export const BookApprovalPanel = ({
  ticket,
  queryKey,
}: {
  ticket: TicketDetail;
  /** The ticket query's key, so an action can write its answer into it. */
  queryKey: QueryKey;
}) => {
  const queryClient = useQueryClient();
  const [isApproving, setIsApproving] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [reason, setReason] = useState('');
  const [reasonTouched, setReasonTouched] = useState(false);

  const { approval } = ticket;
  const canAct = canActOnApproval(ticket);

  const closeApprove = () => {
    setIsApproving(false);
    setReason('');
    setReasonTouched(false);
  };

  /**
   * Both actions answer the ticket detail, so the page shows the new state at
   * once from the answer itself. The queue and its badges moved too.
   */
  const writeTicket = (updated: TicketDetail) => {
    queryClient.setQueryData(queryKey, updated);
    queryClient.invalidateQueries('print-shop-tickets');
    queryClient.invalidateQueries('print-shop-ticket-counts');
  };

  /**
   * A 409 is not a failure to retry: the member (or another operator) decided
   * first. Say so, close the dialog and show what the ticket is now.
   */
  const onConflict = () => {
    ToastNotification({ type: 'failure', message: STRINGS.noLongerAwaiting });
    queryClient.invalidateQueries(queryKey);
    queryClient.invalidateQueries('print-shop-tickets');
    queryClient.invalidateQueries('print-shop-ticket-counts');
    closeApprove();
    setIsResending(false);
  };

  const approveMutation = useMutation(
    (approvalReason: string) =>
      approveTicketOnMemberBehalf(ticket.orderId, { reason: approvalReason }),
    {
      onSuccess: (updated) => {
        writeTicket(updated);
        closeApprove();
        // Said from where DS put the order: Paid, or Waiting for ITP.
        ToastNotification({
          type: 'success',
          message: formatMessage(
            isItpPendingStatus(updated?.status)
              ? STRINGS.approveSuccessItp
              : STRINGS.approveSuccess,
            { orderId: ticket.orderId },
          ),
        });
      },
      onError: (error) => {
        if (isConflict(error)) {
          onConflict();
          return;
        }
        // The dialog stays open with the reason the operator typed, so a 422
        // or a transient failure can be fixed and retried without retyping.
        ToastNotification({
          type: 'failure',
          message: getApiErrorMessage(
            error,
            formatMessage(STRINGS.approveError, { orderId: ticket.orderId }),
          ),
        });
      },
    },
  );

  const resendMutation = useMutation(
    () => resendApprovalEmail(ticket.orderId),
    {
      onSuccess: (updated) => {
        writeTicket(updated);
        setIsResending(false);
        ToastNotification({
          type: 'success',
          message: formatMessage(STRINGS.resendSuccess, {
            date: formatDate(updated.approval?.expiresAt),
          }),
        });
      },
      onError: (error) => {
        if (isConflict(error)) {
          onConflict();
          return;
        }
        ToastNotification({
          type: 'failure',
          message: getApiErrorMessage(
            error,
            formatMessage(STRINGS.resendError, { orderId: ticket.orderId }),
          ),
        });
      },
    },
  );

  if (!approval) {
    return null;
  }

  const reasonError = validateApprovalReason(reason);
  const reasonBounds = {
    min: String(APPROVAL_REASON_MIN_LENGTH),
    max: String(APPROVAL_REASON_MAX_LENGTH),
  };

  const confirmApprove = () => {
    setReasonTouched(true);
    if (reasonError || approveMutation.isLoading) {
      return;
    }
    approveMutation.mutate(reason.trim());
  };

  const confirmResend = () => {
    if (resendMutation.isLoading) {
      return;
    }
    resendMutation.mutate();
  };

  const headline = approvalHeadline(approval, ticket.status);
  const HeadlineIcon = HEADLINE_ICON[headline].icon;
  // One clock reading per render, so the sentence, the fact and the
  // conversation agree on whether the printer e-mail is still being sent.
  const now = Date.now();
  const facts = factsFor(ticket, approval, headline, now);
  const conversation = buildApprovalConversation(ticket, now);
  const copiesText = formatMessage(STRINGS.copiesCount, {
    count: String(ticket.copies ?? ticket.quantity ?? '—'),
  });
  const emailFailed =
    ticket.status !== 'cancelled' &&
    approval.state === 'awaiting' &&
    approval.lastEmail?.status === 'failed';
  // Changes requested: neither action is pushed, so support does not override
  // the member by reflex. Awaiting: approving for them is the main action.
  const approveIsPrimary = ticket.status === 'awaiting_approval';
  const busy = resendMutation.isLoading || approveMutation.isLoading;

  return (
    <section
      data-testid="approval-panel"
      data-state={approval.state}
      data-headline={headline}
      className="my-6 flex flex-col gap-4 rounded-xl border border-neutral-200 bg-white p-5 shadow-sm"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-xs font-bold uppercase tracking-wider text-neutral-500">
          {STRINGS.sectionTitle}
        </h2>
        <span
          data-testid="approval-state"
          className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold ${CHIP_CLASS[headline]}`}
        >
          <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-current" />
          {approvalChipLabel(approval, ticket.status)}
        </span>
      </div>

      <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-5">
        <div
          data-testid="approval-summary"
          className="flex flex-col gap-4 rounded-[10px] bg-neutral-50 p-5 lg:col-span-2"
        >
          <div className="flex items-start gap-3">
            <span
              className={`inline-flex h-10 w-10 flex-none items-center justify-center rounded-full ${HEADLINE_ICON[headline].className}`}
            >
              <HeadlineIcon aria-hidden className="h-5 w-5" />
            </span>
            <div className="flex min-w-0 flex-col gap-1.5">
              <h3
                data-testid="approval-title"
                className="text-3xl font-bold leading-[30px] text-neutral-900"
              >
                {STRINGS.headlines[headline].title}
              </h3>
              <p
                data-testid="approval-next-step"
                className="text-sm leading-5 text-neutral-600"
              >
                {nextStepFor(ticket, approval, headline, now)}
              </p>
            </div>
          </div>

          {emailFailed && (
            <Callout testId="approval-email-failed-hint" tone="danger">
              {STRINGS.emailFailedHint}
            </Callout>
          )}
          {headline === 'changes_requested' && (
            <Callout testId="approval-override-note" tone="info">
              {withBoldToken(STRINGS.changesOverrideNote, 'copies', copiesText)}
            </Callout>
          )}
          {isApprovedWithoutRevision(ticket) && (
            <Callout testId="approval-not-revised-note" tone="info">
              {withBoldToken(STRINGS.notRevisedNote, 'copies', copiesText)}
            </Callout>
          )}

          {canAct && (
            <div className="flex flex-col gap-2">
              <button
                type="button"
                data-testid="approval-approve-button"
                data-variant={approveIsPrimary ? 'filled' : 'outlined'}
                disabled={busy}
                onClick={() => {
                  setReason('');
                  setReasonTouched(false);
                  setIsApproving(true);
                }}
                className={`${ACTION_BASE_CLASS} ${
                  approveIsPrimary ? FILLED_ACTION_CLASS : OUTLINED_ACTION_CLASS
                }`}
              >
                <FiCheck aria-hidden className="h-4 w-4 flex-none" />
                {STRINGS.approveButton}
              </button>
              <button
                type="button"
                data-testid="approval-resend-button"
                data-variant="outlined"
                disabled={busy}
                onClick={() => setIsResending(true)}
                className={`${ACTION_BASE_CLASS} ${OUTLINED_ACTION_CLASS}`}
              >
                <FiRotateCw aria-hidden className="h-4 w-4 flex-none" />
                {STRINGS.resendButton}
              </button>
            </div>
          )}

          {facts.length > 0 && (
            <dl
              data-testid="approval-facts"
              className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 border-t border-neutral-200 pt-4"
            >
              {facts.map((fact) => (
                <React.Fragment key={fact.key}>
                  <dt className="text-[13px] leading-5 text-neutral-600">
                    {fact.label}
                  </dt>
                  <dd
                    data-testid={`approval-fact-${fact.key}`}
                    className="min-w-0 text-[13px] font-semibold leading-5 text-neutral-800"
                  >
                    {fact.value}
                    {fact.hint && (
                      <span className="block text-xs font-normal leading-4 text-neutral-500">
                        {fact.hint}
                      </span>
                    )}
                  </dd>
                </React.Fragment>
              ))}
            </dl>
          )}
        </div>

        <div className="flex min-w-0 flex-col gap-3 pt-1 lg:col-span-3">
          <div className="flex items-center justify-between gap-3">
            <h4 className="text-xs font-bold uppercase tracking-wider text-neutral-500">
              {STRINGS.conversation.title}
            </h4>
            <span className="text-xs text-neutral-500">
              {STRINGS.conversation.order}
            </span>
          </div>
          <ol
            data-testid="approval-conversation"
            className="flex flex-col gap-2.5"
          >
            {conversation.map((entry) => (
              <ConversationEntry key={entry.id} entry={entry} />
            ))}
          </ol>
        </div>
      </div>

      <DefaultModal
        title={STRINGS.approveTitle}
        showModal={isApproving}
        setShowModal={(show) => {
          if (!show) closeApprove();
        }}
        size="md"
      >
        {isApproving && (
          <div className="flex flex-col gap-4">
            <p
              data-testid="approval-approve-intro"
              className="text-sm text-neutral-600"
            >
              {/* An ITP not ready yet holds the order after the approval. */}
              {ticket.itp && ticket.itp.status !== 'ready'
                ? STRINGS.approveIntroItp
                : STRINGS.approveIntro}
            </p>
            <div className="flex flex-col gap-1">
              <label
                htmlFor="approval-reason-input"
                className="text-sm font-semibold text-neutral-800"
              >
                {STRINGS.reasonLabel}
              </label>
              <textarea
                id="approval-reason-input"
                data-testid="approval-reason-input"
                rows={3}
                value={reason}
                placeholder={STRINGS.reasonPlaceholder}
                aria-invalid={reasonTouched && reasonError !== null}
                onChange={(event) => {
                  setReason(event.target.value);
                  setReasonTouched(true);
                }}
                className="rounded-md border border-neutral-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
              {reasonTouched && reasonError ? (
                <span
                  data-testid="approval-reason-error"
                  className="text-xs text-red-600"
                >
                  {formatMessage(
                    reasonError === 'too_short'
                      ? STRINGS.reasonTooShort
                      : STRINGS.reasonTooLong,
                    reasonBounds,
                  )}
                </span>
              ) : (
                <span className="text-xs text-neutral-400">
                  {formatMessage(STRINGS.reasonHint, reasonBounds)}
                </span>
              )}
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="OUTLINED"
                disabled={approveMutation.isLoading}
                onClick={closeApprove}
              >
                {STRINGS.cancelButton}
              </Button>
              <Button
                variant="PRIMARY"
                data-testid="approval-approve-confirm"
                isProcessing={approveMutation.isLoading}
                disabled={reasonError !== null || approveMutation.isLoading}
                onClick={confirmApprove}
              >
                {STRINGS.approveConfirm}
              </Button>
            </div>
          </div>
        )}
      </DefaultModal>

      <DefaultModal
        title={STRINGS.resendTitle}
        showModal={isResending}
        setShowModal={(show) => {
          if (!show) setIsResending(false);
        }}
        size="md"
      >
        {isResending && (
          <div className="flex flex-col gap-4">
            <p
              data-testid="approval-resend-message"
              className="text-sm text-neutral-800"
            >
              {STRINGS.resendMessage}
              {ticket.status === 'changes_requested' &&
                ` ${STRINGS.resendFromChangesMessage}`}
            </p>
            <div className="flex justify-end gap-2">
              <Button
                variant="OUTLINED"
                disabled={resendMutation.isLoading}
                onClick={() => setIsResending(false)}
              >
                {STRINGS.cancelButton}
              </Button>
              <Button
                variant="PRIMARY"
                data-testid="approval-resend-confirm"
                isProcessing={resendMutation.isLoading}
                disabled={resendMutation.isLoading}
                onClick={confirmResend}
              >
                {STRINGS.resendConfirm}
              </Button>
            </div>
          </div>
        )}
      </DefaultModal>
    </section>
  );
};
